import { CommonUtility } from '@utility/common';
import { ErrorMessageConstant } from '@utility/constants/common';
import OrganizationsService from '@utility/services/organization';
import { useCallback, useEffect, useState } from 'react';

export const organizationSettingsHook = (allowed = true) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchData = async (forceRefresh = false) => {
    try {
      setLoading(true);
      const user = await OrganizationsService.settings(forceRefresh);
      setData(user);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (allowed) fetchData();
  }, [allowed]);

  const refresh = () => {
    fetchData(true);
  };

  return {
    data,
    loading,
    error,
    setData,
    refresh,
  };
};

export const organizationsKeysHook = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const refetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await OrganizationsService.keys();
      const payload = res?.data ?? res;
      setData(payload);
      setError('');
      return payload;
    } catch (e) {
      const msg = e?.message || 'Failed to load';
      setError(msg);
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { data, loading, error, refetch };
};

export const LogsHook = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [prevType, setPrevType] = useState('-1');
  const [filter, setFilter] = useState({
    type: '-1',
    limit: 50,
    page: 1,
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = {
        ...filter,
      };
      if (filter.type !== '-1') {
        params.type = filter.type;
      } else {
        // @ts-expect-error - TS2790: The operand of a 'delete' operator must be optional.
        delete params.type;
      }

      if (filter.type !== prevType) {
        setData([]);
        setPrevType(filter.type);
      }

      let { data: result } = await OrganizationsService.logs(params);

      result = result.map((x, i) => {
        if (x.metadata && typeof x.metadata === 'string') {
          const parsedMeta = JSON.parse(x.metadata.replace(/\\"/g, '"'));
          if (
            CommonUtility.isValidArray(parsedMeta.price) ||
            CommonUtility.isValidArray(parsedMeta.items) ||
            CommonUtility.isValidArray(parsedMeta.inventory)
          ) {
            parsedMeta.canExpand = true;
          }
          x.metadata = parsedMeta;
        }
        if ((i + 1) % 50 === 0) {
          x.triggerApi = true;
        }
        return x;
      });

      // @ts-expect-error - TS2345: Argument of type '(d: never[]) => any[]' is not assignable to parameter of type ...
      setData(d => [...d, ...result]);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filter]);

  const updateFilter = (key, value) => {
    setFilter(previousFilter => ({
      ...previousFilter,
      [key]: value,
    }));
  };

  const pageSizeChanged = size => {
    updateFilter('limit', size);
  };

  return {
    data,
    loading,
    error,
    updateFilter,
    filter,
    pageSizeChanged,
  };
};

export const logTypesListHook = (all = false) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetch = async () => {
    try {
      setLoading(true);
      const result = await OrganizationsService.logTypes();
      const list = (result.data || []).map(x => ({
        text: x.label,
        value: x.value,
      }));
      if (all) {
        list.unshift({
          text: 'All',
          value: '-1',
        });
      }
      setData(list);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetch();
  }, []);

  return { data, loading };
};

export const WalamrtLogHook = feedId => {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);

  const fetch = async () => {
    try {
      setLoading(true);
      const { data } = await OrganizationsService.walmartLog(feedId);
      setData(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (feedId) fetch();
  }, [feedId]);

  return { data, loading };
};

export const useLogsSheetsHook = () => {
  const [hasIntersected, setHasIntersected] = useState(true);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState({
    limit: 50,
    page: 1,
  });
  const [error, setError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);

  const fetch = async () => {
    if (filter?.page === 1) {
      setData([]);
      setHasNextPage(true);
    }

    if (filter?.page > 1 && !hasNextPage) {
      return false;
    }

    try {
      setLoading(true);

      const result = await OrganizationsService.fetchLogsSheets(filter);
      const listData = CommonUtility.isValidArray(result?.data)
        ? result?.data
        : [];

      const existing = filter?.page > 1 ? data : [];

      // @ts-expect-error - TS2345: Argument of type 'any[]' is not assignable to parameter of type 'SetStateAction<...
      setData([
        ...existing,
        ...listData.map(d => ({ ...d, id: d?.id || Math.random() })),
      ]);

      setHasNextPage(listData.length > 0);
      if (listData.length) setHasIntersected(false);
    } catch (error) {
      setError(error.message || ErrorMessageConstant.defaultAPIError);
    } finally {
      setLoading(false);
    }
  };

  const updateFilter = (key, value) => {
    setFilter(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const refresh = () => setRefreshKey(Math.random());

  const pageChanged = page => updateFilter('page', page);

  const filterChanged = (key, value) => {
    const temp = {
      ...filter,
      page: 1,
      [key]: value,
    };
    setFilter({ ...temp });
  };

  useEffect(() => {
    fetch();
  }, [filter, refreshKey]);

  return {
    loading,
    data,
    error,
    setData,
    pageChanged,
    filterChanged,
    filter,
    refresh,
    hasIntersected,
    setHasIntersected,
  };
};

export const useOrganizationGlobalSearch = search => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async (searchWith = '') => {
    if (searchWith.length < 3) return null;

    try {
      setLoading(true);
      const result = await OrganizationsService.fetchGlobalSearch(searchWith);
      setData(result);
    } catch (error) {
      setError(error.message || ErrorMessageConstant.defaultAPIError);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(search);
  }, [search]);

  return {
    loading,
    data,
    error,
  };
};
